import { API_BASE_URL } from '@/lib/constants/api';

interface StoredState {
  state: {
    accessToken: string | null;
    sessionToken: string | null;
    impersonateId: string | null;
    auditCompanyId: string | null;
    companyId: string | null;
  };
}

/**
 * Error thrown by `apiFetch` for any non-OK response.
 *
 * - `status` — HTTP status code
 * - `body` — parsed JSON body if the server sent JSON, otherwise the raw
 *   response text (or `null` if the body was empty)
 * - `url` — full URL of the failing request
 * - `message` — best human-readable summary the server provided, or a
 *   fallback like `Request failed with status 500`
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  url: string;

  constructor(
    message: string,
    init: { status: number; body: unknown; url: string },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = init.status;
    this.body = init.body;
    this.url = init.url;
  }
}

export function getCompanyId(): string | null {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const { state } = JSON.parse(raw) as {
      state: { companyId: string | null };
    };
    return state.companyId ?? null;
  } catch {
    return null;
  }
}

export function getAuthHeaders(): Record<string, string> {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return {};
    const { state } = JSON.parse(raw) as StoredState;
    const headers: Record<string, string> = {};
    if (state.accessToken)
      headers.Authorization = `Bearer ${state.accessToken}`;
    if (state.sessionToken) headers['X-Session-Token'] = state.sessionToken;
    if (state.impersonateId)
      headers['x-retraced-impersonate'] = state.impersonateId;
    if (state.auditCompanyId)
      headers['x-retraced-audit-company-id'] = state.auditCompanyId;
    if (state.companyId) headers['x-retraced-company-id'] = state.companyId;
    return headers;
  } catch {
    return {};
  }
}

/**
 * Reads the response body once and returns parsed JSON when possible,
 * otherwise the raw text (or `null` for an empty body). Never throws —
 * malformed JSON falls back to the raw text so error reporting still works.
 */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '');
  if (!text) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  // Some backends return JSON without the proper header — best-effort parse.
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function extractMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    // Nested envelope: { error: { code, message } } / { error: { detail } }
    const nested = obj.error;
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      for (const key of ['message', 'detail', 'title'] as const) {
        const v = n[key];
        if (typeof v === 'string' && v.length > 0) return v;
      }
    }
    for (const key of ['message', 'error', 'detail', 'title'] as const) {
      const v = obj[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  if (typeof body === 'string' && body.length > 0 && body.length < 500) {
    return body;
  }
  return `Request failed with status ${status}`;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  baseUrl = API_BASE_URL,
): Promise<T> {
  const url = `${baseUrl}${path}`;
  const res = await fetch(url, {
    // App data must reflect the server's current state — never serve a
    // stale HTTP cache hit (e.g. dashboard counts after the user just
    // created a new record). Callers can still opt in by passing
    // `cache: 'default'` explicitly.
    cache: 'no-store',
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
      'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...getAuthHeaders(),
      ...init.headers,
    },
  });

  if (res.status === 401) {
    localStorage.removeItem('retraced:context');
    // Docs are public — never bounce a docs visitor to login on a 401.
    if (!window.location.pathname.startsWith('/docs')) {
      const oldFrontend =
        import.meta.env.VITE_OLD_FRONTEND_URL ?? 'http://localhost:3500';
      window.location.href = `${oldFrontend}/login`;
    }
    // Throw to abort the in-flight call; the redirect (if any) is scheduled.
    throw new ApiError('Session expired', { status: 401, body: null, url });
  }

  if (!res.ok) {
    const body = await readBody(res);
    throw new ApiError(extractMessage(body, res.status), {
      status: res.status,
      body,
      url,
    });
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}
